import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from 'mobx';
import intl from 'react-intl-universal';
import { injector } from 'Injector';

import { QUESTIONARY_SERVICE, QuestionaryService } from 'assignment/questionary/service';
import { Article, ARTICLE_SERVICE_KEY, Assignment, Question } from 'assignment/Assignment';
import { Answer, Questionary, RedirectData } from 'assignment/questionary/Questionary';
import { STORAGE_INTERACTOR_KEY, StorageInteractor } from 'utils/storageInteractor';
import { ArticleService, AssignmentService, ASSIGNMENT_SERVICE } from '../../service';
import { USER_SERVICE, UserService } from '../../../user/UserService';
import { reportFailure } from 'utils/reportFailure';

const COVER_INDEX = -2;

export class CurrentQuestionaryStore {

  private questionaryService: QuestionaryService = injector.get<QuestionaryService>(QUESTIONARY_SERVICE);
  private userService: UserService = injector.get<UserService>(USER_SERVICE);
  private assignmentService: AssignmentService = injector.get(ASSIGNMENT_SERVICE);
  private storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);
  private articleService: ArticleService = injector.get<ArticleService>(ARTICLE_SERVICE_KEY);

  @observable public isLoading: boolean = false;
  @observable public loadFailed: boolean = false;
  @observable public currentQuestionary: Questionary | null = null;
  @observable public isMultipleQuestion: boolean = false;
  @observable public assignment: Assignment | null = null;
  @observable public questions: Array<Question> = [];
  @observable public answers: Array<Answer> = [];
  @observable public showValidationErrors: boolean = false;
  @observable public currentQuestionIndex: number = 0;
  @observable public isLoadingArticles: boolean = false;
  public isArrowsTooltipVisible: boolean = true;
  @observable public isStartedAssignment: boolean = false;
  @observable public relatedArticles: Array<Article> = [];
  public allArticlesread: boolean = false;

  constructor() {
    makeObservable(this);
  }

  /**
   * Whether the questionary loaded. A failure is reported here and leaves `loadFailed` set; it
   * used to leave `isLoading` set instead, and the page on its loading cover for good.
   */
  private async loadQuestionary(fetch: () => Promise<Questionary>): Promise<boolean> {
    runInAction(() => {
      this.isLoading = true;
      this.loadFailed = false;
    });
    try {
      const questionary = await fetch();
      runInAction(() => {
        this.currentQuestionary = questionary;
        this.assignment = questionary.assignment;
        this.questions = questionary.assignment.questions;
        this.answers = questionary.answers;
        this.isLoading = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.loadFailed = true;
      });
      reportFailure(error);
      return false;
    }
  }

  public async createQuestionaryByAssignmentId(id: number, redirectData?: RedirectData) {
    return this.loadQuestionary(async () => (redirectData !== undefined
      ? this.questionaryService.getNewQuestionaryByAssignmentIdFromTeachingPath(id, redirectData)
      : this.questionaryService.getNewQuestionaryByAssignmentId(id)));
  }

  public async getQuestionaryById(assignmentId: number) {
    return this.loadQuestionary(async () => this.questionaryService.getAssignmentQuestionaryById(assignmentId));
  }

  public async getQuestionaryByIdPreview(assignmentId: number) {
    return this.loadQuestionary(async () => this.questionaryService.getAssignmentQuestionaryByIdPreview(assignmentId));
  }

  public getCurrentUser = () => this.userService.getCurrentUser();

  @computed
  public get questionTitlesList() {
    return this.questions.map(question => question.title);
  }

  /**
   * A plain getter, deliberately: not `@computed`.
   *
   * A computed caches its value until an *observable* dependency changes, and `intl.get` is
   * not observable. As a computed this list kept the previous locale's "Review & Submit"
   * after a language switch, because the questions had not changed — the same defect I
   * shipped and corrected in #149/#150 for the teacher-guidance label.
   *
   * `questionTitlesList` above is still a computed, and should be: it derives from observable
   * state only. Reading it from here keeps that memoisation, so dropping the decorator costs
   * a spread of a short array per read and nothing else.
   */
  public get questionTitlesListWithSubmit() {
    return [
      ...this.questionTitlesList,
      intl.get('current_assignment_page.Review & Submit')
    ] as Array<string>;
  }

  @computed
  public get currentQuestion() {
    if (this.currentQuestionIndex >= 0) {
      return this.questions[this.currentQuestionIndex];
    }
    return null;
  }

  @computed
  public get currentAnswer(): Answer {
    return this.answers[this.currentQuestionIndex];
  }

  @computed
  public get deadline() {
    return this.assignment!.deadline;
  }

  @computed
  public get featuredImage() {
    return (this.assignment) ? this.assignment.featuredImage : '';
  }

  @computed
  public get backgroundImage() {
    return (this.assignment) ? this.assignment.backgroundImage : '';
  }

  @computed
  public get authoravatar() {
    return this.assignment!.authoravatar;
  }

  @computed
  public get author() {
    return this.assignment!.author;
  }

  public get numberOfQuestions(): number {
    return this.questions.length;
  }

  @computed
  public get relatedAllArticles(): Array<Article> {
    return this.relatedArticles;
  }

  public get numberOfAnsweredQuestions(): number {
    const answersWithValue = this.answers.filter(answer => toJS(answer.value).length);
    return answersWithValue.length;
  }

  @action
  public getIsReadArticles = (): boolean =>
    this.relatedArticles.some(i => i.isRead === true)

  /**
   * Resolves false, having told the student, when the server did not record the read: the
   * article then stays unread here too, rather than counting towards unlocking the questions
   * on this screen and not on the next.
   */
  public async setReadStatusToArticle(idArticle: number, levelId: number, graduation: number, isStudent: boolean): Promise<boolean> {
    if (isStudent) {
      try {
        await this.questionaryService.setReadStatusArticle(this.assignment!.id, this.currentQuestionary!.idRevision!, idArticle, levelId, graduation);
      } catch (error) {
        reportFailure(error);
        return false;
      }
    }
    this.markArticleRead(idArticle);
    return true;
  }

  @action
  public markArticleRead(idArticle: number) {
    let indexArticle = -1;
    const article = this.relatedArticles.find((article, index) => {
      if (article.id === idArticle) {
        indexArticle = index;
        return article;
      }
      return null;
    });
    article!.isRead = true;
    this.relatedArticles.splice(indexArticle, 1, article!);
    const readArticles = this.relatedArticles.filter(article => article.isRead);
    if (this.relatedArticles.length === readArticles.length) {
      this.currentQuestionary!.isQuestionaryChanged = true;
      this.setCurrentQuestion(0);
    }
  }

  @action
  public setStartedAssignment(value: boolean) {
    this.isStartedAssignment = value;
  }

  @action
  public async getRelatedArticles() {
    this.setCurrentQuestion(COVER_INDEX);
    if (this.assignment!) {
      try {
        const ids = this.assignment.relatedArticles ? this.assignment.relatedArticles.map(i => i.id) : [];
        if (ids && ids.length && ids.length > 0) {
          this.isLoadingArticles = false;
          const articles = await this.articleService.getArticlesByIds(ids);
          const allArticles = this.assignment.relatedArticles.map((article) => {
            const fullArticles = articles.find(item => item.id === article.id);
            return {
              ...article,
              ...fullArticles,
              correspondingLevelArticleId: article.correspondingLevelArticleId,
              isRead: article.isRead
            };
          });
          return runInAction(() => {
            this.isLoadingArticles = true;
            this.relatedArticles = allArticles;

            return this.relatedArticles;
          });
        }
        return this.relatedArticles = [];
      } catch (e) {
        // Reported, not rethrown: the page awaits this while it mounts, and stopped on its cover.
        // The list is left empty and finished loading; with nothing read, the questions stay
        // locked, as they would with the articles still unread (#216).
        reportFailure(e);
        runInAction(() => { this.isLoadingArticles = true; });
        return this.relatedArticles;
      }
    } else {
      return this.relatedArticles = [];
    }

  }

  @action
  public setCurrentQuestion = (index: number) => {
    this.currentQuestionIndex = index;
  }

  @action
  public publishQuestionary = async (redirectData?: RedirectData): Promise<void> => {
    this.showValidationErrors = true;
    reaction(() => this.currentQuestionary!.isValid, (isValid, previousValue, reaction) => {
      if (isValid) {
        this.showValidationErrors = false;
        reaction.dispose();
      }
    });
    return this.currentQuestionary!.publish(redirectData);
  }

  public async deleteQuestionary() {
    return this.questionaryService.deleteQuestionary();
  }

  public async revertQuestionary() {
    const response = await this.questionaryService.revertQuestionary();
    this.currentQuestionary = response;
    this.answers = response.answers;
    this.relatedArticles = this.relatedArticles.map(article => ({ ...article, isRead: false }));
  }

  @action
  public handleShowArrowsTooltip = (isVisible: boolean) => {
    this.isArrowsTooltipVisible = isVisible;
  }

  @action
  public async downloadTeacherGuidancePDF(id: number) {
    return this.assignmentService.downloadTeacherGuidancePDF(id);
  }
}
